import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, LoaderCircle, X } from "lucide-react";
import { fetchEventDetail, fetchGalleryImages } from "@/api/events";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";

const CATEGORIES = ["All", "Matha", "Festivals", "Pontiffs", "Annadana", "Go Shala", "Pooja", "Sanchara"];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { t, lv } = useLang();

  useEffect(() => { fetchGalleryImages().then(setItems).catch(() => {}); }, []);

  const filtered = useMemo(
    () => (category === "All" ? items : items.filter(i => i.category === category)),
    [items, category]
  );

  const openLightbox = async (item) => {
    setLightboxLoading(true);
    setLightboxIndex(0);
    setLightbox({
      ...item,
      images: item.image ? [item.image] : [],
      title: lv(item, "title"),
      category: lv(item, "category"),
    });

    try {
      const detail = await fetchEventDetail(item.id);
      const images = detail.images?.length ? detail.images : (item.image ? [item.image] : []);

      setLightbox({
        ...item,
        ...detail,
        images,
        title: detail.title || lv(item, "title"),
        category: lv(item, "category"),
      });
    } catch {
      setLightbox(current => current || {
        ...item,
        images: item.image ? [item.image] : [],
        title: lv(item, "title"),
        category: lv(item, "category"),
      });
    } finally {
      setLightboxLoading(false);
    }
  };

  const closeLightbox = () => {
    setLightbox(null);
    setLightboxLoading(false);
    setLightboxIndex(0);
  };

  const lightboxImages = lightbox?.images || [];
  const activeImage = lightboxImages[lightboxIndex] || lightbox?.image || "";
  const showControls = lightboxImages.length > 1;

  const goToPrevious = () => {
    setLightboxIndex(index => (index - 1 + lightboxImages.length) % lightboxImages.length);
  };

  const goToNext = () => {
    setLightboxIndex(index => (index + 1) % lightboxImages.length);
  };

  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow={t.gallery.eyebrow} title={t.gallery.title} sub={t.gallery.sub} />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="gallery-filter">
            {CATEGORIES.map(c => (
              <button
                key={c}
                data-testid={`gallery-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`px-5 py-2 rounded-full text-[13px] font-medium tracking-wide transition-colors ${
                  category === c ? "bg-[#7B1818] text-[#FDFBF7]" : "bg-white text-[#2A2118] border border-[#E5DAC7] hover:bg-[#F4EBDB]"
                }`}
              >
                {t.gallery.cats[c]}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((g, i) => (
              <button
                key={g.id}
                data-testid={`gallery-image-${i}`}
                onClick={() => openLightbox(g)}
                className="reveal overflow-hidden rounded-2xl bg-[#E5DAC7] aspect-square group relative"
              >
                <img src={g.image} alt={lv(g, "title")} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFD98A]">{lv(g, "category")}</div>
                    <div className="text-white text-sm font-medium leading-snug mt-1">{lv(g, "title")}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center text-[#5C4D42] py-16">{t.gallery.empty}</div>
          )}
        </div>
      </section>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          onClick={closeLightbox}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <button
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/95 flex items-center justify-center text-[#7B1818]"
          >
            <X size={20} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              {lightboxLoading && (
                <div className="absolute inset-0 z-10 rounded-2xl bg-black/30 flex items-center justify-center">
                  <LoaderCircle size={36} className="text-white animate-spin" />
                </div>
              )}
              <img src={activeImage} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-2xl" />
              {showControls && (
                <>
                  <button
                    type="button"
                    onClick={goToPrevious}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 flex items-center justify-center text-[#7B1818]"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={goToNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 flex items-center justify-center text-[#7B1818]"
                    aria-label="Next image"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
            <div className="text-center mt-4">
              <div className="eyebrow text-[#FFD98A]">{lightbox.category}</div>
              <div className="font-display text-white text-2xl mt-1">{lightbox.title}</div>
              {showControls && (
                <div className="mt-4 flex justify-center gap-2">
                  {lightboxImages.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      onClick={() => setLightboxIndex(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === lightboxIndex ? "w-8 bg-[#FFD98A]" : "w-2.5 bg-white/40"
                      }`}
                      aria-label={`View slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
